// Court scoreboard settings — how early the next group takes the board.
//
// Self-contained like GroupRentalInvitePanel: it loads and saves its own doc,
// settings/scoreboard, so the page only renders it. The endpoint that reads it
// is scoreboardDisplay in functions/index.js; the rules are functions/scoreboard.js.
import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logError } from '../utils/errorLogger';
import { Paper, Typography, TextField, Alert, Box } from '@mui/material';

const DEFAULT_CHANGEOVER = 15;
const MAX_CHANGEOVER = 120;

export default function ScoreboardSettingsPanel() {
  const [minutes, setMinutes] = useState('');
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'settings', 'scoreboard'))
      .then(snap => {
        const v = snap.exists() ? snap.data().changeoverMinutes : undefined;
        const n = v === undefined || v === null ? DEFAULT_CHANGEOVER : v;
        setMinutes(String(n));
        setSaved(n);
      })
      .catch(e => { logError('ScoreboardSettingsPanel', e.message, e.stack); setError('Could not load scoreboard settings.'); });
  }, []);

  const save = async () => {
    const n = Number(minutes);
    if (minutes === '' || !Number.isFinite(n) || n < 0 || n > MAX_CHANGEOVER) {
      setError(`Enter a number of minutes from 0 to ${MAX_CHANGEOVER}.`);
      return;
    }
    const value = Math.round(n);
    if (value === saved) return;
    setError('');
    try {
      await setDoc(doc(db, 'settings', 'scoreboard'), { changeoverMinutes: value }, { merge: true });
      setSaved(value);
      setMinutes(String(value));
      setNotice('Scoreboard changeover saved.');
      setTimeout(() => setNotice(''), 2000);
    } catch (e) {
      logError('ScoreboardSettingsPanel', e.message, e.stack);
      setError('Failed to save scoreboard settings.');
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee', mb: 3, backgroundColor: '#f9fafb' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
        Court Scoreboards
      </Typography>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
        Each court's scoreboard shows who has the court. The next group takes over the board this many
        minutes before their start, so they see their name as they walk up. With nothing booked, the board
        shows the next reservation today.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      <Box sx={{ maxWidth: 320 }}>
        <TextField
          fullWidth label="Changeover (minutes before start)" type="number"
          value={minutes}
          onChange={e => setMinutes(e.target.value)}
          onBlur={save}
          helperText={`Default ${DEFAULT_CHANGEOVER}. 0 = switch exactly at the start time.`}
          inputProps={{ min: 0, max: MAX_CHANGEOVER, step: 5 }}
          sx={{ backgroundColor: 'white', borderRadius: 1 }}
        />
      </Box>
    </Paper>
  );
}
